import React, { useMemo } from 'react'
import { ApolloClient, gql, useQuery } from '@apollo/client'
import { Maybe } from 'graphql/jsutils/Maybe'
import LazyHydrate from 'react-lazy-hydration'
import { ChevronLeftIcon } from '@/atoms/Icons/ChevronLeftIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { HeadingInvisible } from '@/atoms/Text'
import { PayItForwardButton } from '@/molecules/PayItForwardButton'
import { FullScreenAngelPlayer } from '@/organisms/FullScreenAngelPlayer'
import { incrementUserViewCount } from '@/organisms/GetAppBanner/GetAppBannerUtil'
import { HorizontalShopifyMerchScroller } from '@/organisms/HorizontalShopifyMerchScroller'
import { VideoGrid } from '@/organisms/VideosGrid/VideoGrid'
import { WatchDepartments } from '@/organisms/WatchDepartments'
import { ShopifyStoreConfig } from '@/services/EarlyAccessService'
import { Project, VideoMetaData } from '@/services/ProjectsService'
import { PageDataContext, PageModel } from '@/services/RenderService'
import { ProjectTheme } from '@/types/codegen-contentful'
import { useSafeTrack } from '@/utils/analytics'
import { getProjectTheme } from '@/utils/project-themes'
import { useTranslate } from '@/utils/translate/translate-client'
import { WatchProjectContext } from '../WatchProjectView/WatchProjectContext'
import { useVideoAsset } from './useVideoAsset'

export interface WatchVideoViewProps {
  projectName: string
  projectSlug: string
  video: VideoMetaData
  hydraClient?: ApolloClient<object>
  shopifyStore?: ShopifyStoreConfig
  projectData: Project
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
  contentfulProjectTheme: Maybe<ProjectTheme>
  isGuildMember?: boolean
}

export const getVideoWatchPosition = gql`
  query getVideoWatchPosition($guid: ID!) {
    video(guid: $guid) {
      watchPosition {
        position
      }
    }
  }
`

interface VideoWatchPositionResponse {
  video: {
    watchPosition: {
      position: number
    }
  }
}

export const WatchVideoView: React.FC<WatchVideoViewProps> = (props) => {
  const { video, projectName, projectSlug, shopifyStore } = props
  const { data, loading } = useQuery<VideoWatchPositionResponse>(getVideoWatchPosition, {
    variables: { guid: video.guid },
    errorPolicy: 'all',
  })
  const { asset } = useVideoAsset({ video: { ...video, ...data?.video }, projectSlug })
  const track = useSafeTrack()

  const { t } = useTranslate('watch')

  const merchAnalytics = useMemo(() => {
    return { context: 'watch-videos', content_id: video.guid, program: projectSlug }
  }, [projectSlug, video.guid])

  const projectTheme = getProjectTheme(props.projectSlug) ?? {}

  const backToProject = t('backToProject', 'Back to {{projectName}}', { projectName })
  return (
    <WatchProjectContext.Provider value={{ projectTheme, project: props.projectData }}>
      <div className="bg-core-gray-950 pb-32 pt-24 text-white">
        <PaddedContainer>
          <nav title={backToProject} className="mb-4">
            <InternalLink href={`/watch/${projectSlug}`} className="flex cursor-pointer items-center">
              <ChevronLeftIcon className="mr-2" /> {backToProject}
            </InternalLink>
          </nav>
        </PaddedContainer>
        <main>
          <PaddedContainer>
            <FullScreenAngelPlayer autoplay asset={!loading ? asset : undefined} onStart={incrementUserViewCount}>
              <div className="mt-6 flex flex-wrap px-4 md:flex-nowrap md:px-0">
                <div>
                  <h1 aria-label="player-episode-mapping" className="text-gray-500">
                    {asset?.sourceConfig?.title}
                  </h1>
                  <div aria-label="player-episode-title" className="font-semibold">
                    {asset?.sourceConfig?.subtitle}
                  </div>
                </div>
                <div className="mt-8 w-full md:ml-auto md:mt-0 md:w-fit ">
                  <PayItForwardButton
                    className="w-full whitespace-nowrap"
                    onClick={() => {
                      track('Clicked Pay It Forward From Video View', {
                        asset_id: props.video.guid,
                        program: props.projectSlug,
                        subtitle: props.video.subtitle,
                        title: props.video.title,
                      })
                    }}
                    projectSlug={projectSlug}
                  />
                </div>
              </div>
            </FullScreenAngelPlayer>
          </PaddedContainer>
        </main>
        {isSeasonTrailer(props.video) ? (
          <WatchDepartments
            projectData={props.projectData}
            loadingProjectData={false}
            downloadables={[]}
            slug={props.projectSlug}
            singleStory={false}
            page={props.page}
            pageDataContext={props.pageDataContext}
            contentfulProjectTheme={props.contentfulProjectTheme}
            isGuildMember={props.isGuildMember}
          />
        ) : (
          <PaddedContainer>
            <section className="mt-8 w-full">
              <HeadingInvisible>{t('listOfVideos', 'List of Videos')}</HeadingInvisible>
              <LazyHydrate whenVisible>
                <VideoGrid projectSlug={projectSlug} page={video.page} excludeGuid={video.guid} />
              </LazyHydrate>
            </section>
          </PaddedContainer>
        )}
        <section>
          <hr className="my-8 border-gray-800" />
          {shopifyStore && (
            <LazyHydrate whenVisible>
              <HorizontalShopifyMerchScroller
                storeUrl={shopifyStore.storeUrl}
                accessToken={shopifyStore.accessToken}
                handle={shopifyStore.handle}
                storeHref={shopifyStore?.storeHref}
                analytics={merchAnalytics}
                slug={projectSlug}
                padding
              />
            </LazyHydrate>
          )}
        </section>
      </div>
    </WatchProjectContext.Provider>
  )
}

function isSeasonTrailer(video: VideoMetaData) {
  return /season/i.test(video.title) && /trailer/i.test(video.title)
}
